import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  List,
  Button,
  Paper,
  IconButton,
  Badge,
  Alert,
  Pagination,
  CircularProgress,
  Divider,
  Link as MuiLink,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  NotificationsActive as NotificationsActiveIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  MarkEmailRead as MarkEmailReadIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';

// Types
interface Notification {
  id: string;
  type: 'workout' | 'achievement' | 'trainer_update' | 'system' | 'sync';
  title: string;
  message: string;
  timestamp: string;
  read: boolean;
  actionUrl?: string;
  metadata?: Record<string, unknown>;
}

interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead: (id: string) => void;
  onDelete: (id: string) => void;
}

// NotificationItem Component
const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onMarkAsRead,
  onDelete,
}) => {
  const navigate = useNavigate();

  const getNotificationIcon = (type: Notification['type']) => {
    switch (type) {
      case 'workout':
        return <NotificationsActiveIcon color="primary" />;
      case 'achievement':
        return <CheckCircleIcon color="success" />;
      case 'trainer_update':
        return <NotificationsIcon color="info" />;
      case 'sync':
        return <NotificationsIcon color="warning" />;
      default:
        return <NotificationsIcon />;
    }
  };

  const handleActionClick = () => {
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
    }
  };

  return (
    <Paper
      elevation={notification.read ? 0 : 2}
      sx={{
        p: 2,
        mb: 1,
        backgroundColor: notification.read ? 'transparent' : 'action.hover',
        border: '1px solid',
        borderColor: 'divider',
        transition: 'all 0.2s',
        '&:hover': {
          backgroundColor: 'action.selected',
        },
      }}
    >
      <Box display="flex" alignItems="flex-start" gap={2}>
        <Box sx={{ mt: 0.5 }}>{getNotificationIcon(notification.type)}</Box>

        <Box flex={1}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
            <Typography
              variant="subtitle1"
              fontWeight={notification.read ? 'normal' : 'bold'}
              sx={{ mb: 0.5 }}
            >
              {notification.title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {format(new Date(notification.timestamp), 'MMM d, yyyy h:mm a')}
            </Typography>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {notification.message}
          </Typography>

          {notification.actionUrl && (
            <MuiLink
              component="button"
              variant="body2"
              onClick={handleActionClick}
              sx={{ textAlign: 'left' }}
            >
              View Details
            </MuiLink>
          )}
        </Box>

        <Box display="flex" flexDirection="column" gap={1}>
          {!notification.read && (
            <IconButton
              size="small"
              onClick={() => onMarkAsRead(notification.id)}
              title="Mark as read"
            >
              <MarkEmailReadIcon fontSize="small" />
            </IconButton>
          )}
          <IconButton
            size="small"
            onClick={() => onDelete(notification.id)}
            title="Delete"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    </Paper>
  );
};

// Main NotificationsCenterPage Component
const NotificationsCenterPage: React.FC = () => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState<'unread' | 'all'>('unread');
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pushPermission, setPushPermission] = useState<NotificationPermission>('default');
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  const itemsPerPage = 10;

  // Check push notification permission
  useEffect(() => {
    if ('Notification' in window) {
      setPushPermission(Notification.permission);
    }
  }, []);

  // Offline detection
  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Fetch notifications
  useEffect(() => {
    const fetchNotifications = async () => {
      setLoading(true);
      try {
        // Simulated API call - replace with actual RTK Query endpoint
        await new Promise((resolve) => setTimeout(resolve, 1000));

        // Mock data
        const mockNotifications: Notification[] = [
          {
            id: '1',
            type: 'workout',
            title: 'New Workout Assigned',
            message: 'Your trainer has assigned a new interval workout for tomorrow.',
            timestamp: new Date().toISOString(),
            read: false,
            actionUrl: '/athlete/workouts/123',
          },
          {
            id: '2',
            type: 'achievement',
            title: 'New Personal Record!',
            message: 'Congratulations! You set a new 5K PR: 22:15',
            timestamp: new Date(Date.now() - 3600000).toISOString(),
            read: false,
            actionUrl: '/athlete/activities/456',
          },
          {
            id: '3',
            type: 'trainer_update',
            title: 'Trainer Message',
            message: 'Great progress this week! Keep up the good work.',
            timestamp: new Date(Date.now() - 7200000).toISOString(),
            read: true,
          },
          {
            id: '4',
            type: 'sync',
            title: 'Strava Sync Complete',
            message: '5 new activities synced from Strava.',
            timestamp: new Date(Date.now() - 86400000).toISOString(),
            read: true,
            actionUrl: '/athlete/activities',
          },
        ];

        setNotifications(mockNotifications);
        setTotalPages(Math.ceil(mockNotifications.length / itemsPerPage));
      } catch (error) {
        console.error('Failed to fetch notifications:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
  }, [page, tabValue]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: 'unread' | 'all') => {
    setTabValue(newValue);
    setPage(1);
  };

  const handleMarkAsRead = (id: string) => {
    setNotifications((prev) =>
      prev.map((notif) => (notif.id === id ? { ...notif, read: true } : notif))
    );
  };

  const handleDelete = (id: string) => {
    setNotifications((prev) => prev.filter((notif) => notif.id !== id));
  };

  const handleMarkAllAsRead = () => {
    setNotifications((prev) => prev.map((notif) => ({ ...notif, read: true })));
  };

  const handleRequestPushPermission = async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      setPushPermission(permission);

      if (permission === 'granted') {
        // Register push subscription with service worker
        if ('serviceWorker' in navigator) {
          try {
            const registration = await navigator.serviceWorker.ready;
            await registration.pushManager.subscribe({
              userVisibleOnly: true,
              applicationServerKey: 'YOUR_VAPID_PUBLIC_KEY', // Replace with actual VAPID key
            });
          } catch (error) {
            console.error('Failed to subscribe to push notifications:', error);
          }
        }
      }
    }
  };

  const filteredNotifications =
    tabValue === 'unread'
      ? notifications.filter((n) => !n.read)
      : notifications;

  const paginatedNotifications = filteredNotifications.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {isOffline && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          You are currently offline. Notifications may not be up to date.
        </Alert>
      )}

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Badge badgeContent={unreadCount} color="error">
            <NotificationsIcon fontSize="large" />
          </Badge>
          <Typography variant="h4" component="h1">
            Notifications
          </Typography>
        </Box>

        <IconButton
          onClick={() => navigate('/settings')}
          title="Notification Settings"
        >
          <SettingsIcon />
        </IconButton>
      </Box>

      {pushPermission !== 'granted' && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={handleRequestPushPermission}>
              Enable
            </Button>
          }
        >
          Enable push notifications to receive real-time updates about workouts, achievements,
          and trainer messages.
        </Alert>
      )}

      <Paper sx={{ mb: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab
              label={`Unread (${unreadCount})`}
              value="unread"
            />
            <Tab label="All" value="all" />
          </Tabs>

          {unreadCount > 0 && (
            <Button
              startIcon={<MarkEmailReadIcon />}
              onClick={handleMarkAllAsRead}
              size="small"
            >
              Mark All Read
            </Button>
          )}
        </Box>
        <Divider />
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : paginatedNotifications.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <NotificationsIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            {tabValue === 'unread' ? 'No unread notifications' : 'No notifications'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {tabValue === 'unread'
              ? "You're all caught up!"
              : 'Notifications will appear here when you receive them.'}
          </Typography>
        </Paper>
      ) : (
        <>
          <List sx={{ mb: 2 }}>
            {paginatedNotifications.map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onMarkAsRead={handleMarkAsRead}
                onDelete={handleDelete}
              />
            ))}
          </List>

          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_event, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      <Box mt={3} textAlign="center">
        <MuiLink
          component="button"
          variant="body2"
          onClick={() => navigate('/settings')}
        >
          Manage notification preferences
        </MuiLink>
      </Box>
    </Container>
  );
};

export default NotificationsCenterPage;